import {
  DownOutlined,
  MoreOutlined,
  QuestionCircleOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Flex, Modal, Space, message } from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';
import { useDeleteUser, useUpdateRole } from '../../api/Onboarding';
import useAuth from '../../auth/useAuth';
import SearchableTable from '../../components/SearchableTable';
import { UserRoleType } from '../../constants';
import { queryType } from '../../types';
import AddUserModal from './AddUserModal';

const StyledButton = styled(Button)`
  padding-left: 0;
`;

const Toolbar = styled(Flex)`
  margin-bottom: 12px;
`;

function UsersTab({ users, organizationId }) {
  const { user } = useAuth();
  const [addUserOpen, setAddUserOpen] = useState(false);
  const { mutate: updateRole, isLoading: updateRoleLoading } = useUpdateRole();
  const { mutate: deleteUser, isLoading: deleteUserLoading } = useDeleteUser();

  const handleUpdateRole = ({ userId, role }) => {
    if (users.data.find((u) => u.id === userId).role !== role) {
      updateRole(
        { userId, organizationId, data: { role } },
        { onSuccess: () => message.success('User type updated') },
      );
    }
  };

  const handleDeleteUser = (userId) => {
    deleteUser(
      { userId, organizationId },
      { onSuccess: () => message.success('User successfully deleted') },
    );
  };

  const showConfirmDeleteModal = (userId) => {
    Modal.confirm({
      title: 'Are you sure?',
      autoFocusButton: 'ok',
      content:
        "This can't be undone. If you want this user back, you'll need to invite them again.",
      okText: 'Delete',
      onOk: () => handleDeleteUser(userId),
      okButtonProps: { danger: true, type: 'primary' },
    });
  };

  const userItems = [
    {
      key: UserRoleType.VIEWER,
      label: UserRoleType.VIEWER,
    },
    {
      key: UserRoleType.EDITOR,
      label: UserRoleType.EDITOR,
    },
    {
      key: UserRoleType.ADMINISTRATOR,
      label: UserRoleType.ADMINISTRATOR,
    },
  ];

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: (
        <>
          User Type
          <Button
            type="link"
            icon={<QuestionCircleOutlined />}
            onClick={() => window.Intercom('show')}
          />
        </>
      ),
      dataIndex: 'role',
      render: (_, record) => (
        <Dropdown
          menu={{
            items: userItems,
            onClick: (item) => {
              handleUpdateRole({ userId: record.id, role: item.key });
            },
          }}
          disabled={record.email === user.email}
        >
          <StyledButton type="link">
            <Space>
              {record.role}
              <DownOutlined />
            </Space>
          </StyledButton>
        </Dropdown>
      ),
    },
    {
      title: 'Last active',
      dataIndex: 'last_active',
      render: (date) => (date ? dayjs(date).format('MMM D, YYYY h:mm A') : '--'),
      sorter: (a, b) => new Date(a.last_active) - new Date(b.last_active),
    },
    {
      key: 'action',
      width: 50,
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: record.id,
                label: 'Delete user',
              },
            ],
            onClick: () => showConfirmDeleteModal(record.id),
          }}
          disabled={record.email === user.email}
        >
          <Button type="text">
            <MoreOutlined />
          </Button>
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <Toolbar justify="flex-end">
        <Button
          type="primary"
          icon={<UserAddOutlined />}
          onClick={() => setAddUserOpen(true)}
          data-cy="add-user-button"
        >
          Add user
        </Button>
      </Toolbar>
      <SearchableTable
        columns={columns}
        baseData={users.data}
        rowKey="id"
        searchPlaceholder="Search users..."
        loading={updateRoleLoading || deleteUserLoading}
        searchColumns={['name']}
      />
      <AddUserModal
        open={addUserOpen}
        onClose={() => setAddUserOpen(false)}
        organizationId={organizationId}
      />
    </>
  );
}

UsersTab.propTypes = {
  users: queryType.isRequired,
  organizationId: PropTypes.number.isRequired,
};

export default UsersTab;
